import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useWebSocket } from "react-use-websocket/dist/lib/use-websocket"
import { API_URL, WS_URL2 } from "../utils/config"
import { ReadyState } from "react-use-websocket"
import axios from "axios"

export interface CounterpartyPresence {
  isOnline: boolean
  isInChat: boolean
  activeChatId?: number | null
  lastActiveAt?: string | null
}

export const SocketContext = createContext<any>(null)

// PERF-09: sozlamalar obyekti ham MODUL darajasida — komponent ichida yozilsa har
// renderda yangi obyekt bo'lib, u ham ulanishni qayta qurishga sabab bo'lardi.
const WS_OPTIONS = {
  protocols: ["aspnetcore-ws.plaintext", "aspnetcore-ws.aspnetcore-ws.json"],
  shouldReconnect: () => true,
  reconnectAttempts: Number.MAX_SAFE_INTEGER,
  reconnectInterval: 3000,
  retryOnError: true,
  // Tog'a #342 (katalog #6024): qo'ng'iroq signali har bir kadrda alohida hodisa bo'lib uzatiladi (lastMessage tez kelgan xabarlarni birlashtiradi)
  onMessage: (e: MessageEvent) => {
    try {
      const s = String(e?.data || "")
      if (s.indexOf("\"nameFunction\":\"call\"") < 0) return
      window.dispatchEvent(new CustomEvent("crm-call", { detail: JSON.parse(s) }))
    } catch {}
  },
}

export function SocketProvider({ children }: { children: React.ReactNode }) {
  // Egasi (2026-09-02): «chat ishlamayotgandek tuyuladi» — ulanish uzilsa (uyqu, tarmoq, server restart) qayta
  // ULANMASDI: bildirishnomalar sahifa yangilanguncha to'xtab qolardi. Endi cheksiz qayta ulanish (har 3 s).
  // audit AUTH-01 (2026-09-03): jonli ulanish endi TOKEN bilan tasdiqlanadi — brauzer WebSocket sarlavha
  // yubora olmagani uchun token query orqali ketadi (server imzosini tekshiradi).
  // ⛔ PERF-09 (2026-09-03) — KRITIK TUZATISH.
  // Yuqoridagi AUTH-01 o'zgarishida `wsUrl` oddiy funksiya sifatida komponent ICHIDA
  // yozilgan edi. react-use-websocket ulanish effektini `url` ning AYNIYATI bo'yicha
  // kuzatadi (use-websocket.js: deps [url, connect, stringifiedQueryParams, sendMessage]).
  // Har renderda YANGI funksiya obyekti tug'ilgani uchun soket har renderda uzilib
  // qaytadan ochilardi; qayta ochilish esa setReadyState chaqirib yana render qilardi —
  // to'xtovsiz halqa. O'lchandi: sekundiga ~2500 ta yangi WebSocket va ~5000 ta render,
  // brauzer oqimining ~88% i shunga ketardi. Telefon/planshet qizib ketishi shundan.
  // Yechim: ayniyat FAQAT token o'zgarganda yangilanadi.
  //
  // PERF-09b: dastlab `useCallback([])` qilingandi, lekin u haddan tashqari qat'iy edi —
  // ilova login sahifasida tokensiz ulanib, kirgandan keyin QAYTA ULANMASDI. Natijada
  // soket kimligi aniqlanmagan holda qolardi (server foydalanuvchini tokendan oladi),
  // ya'ni chat va bildirishnomalar ishlamay qolardi.
  // Endi token localStorage'dan kuzatiladi va u o'zgarganda (kirish/chiqish) ayniyat
  // BIR MARTA yangilanadi — soket bir marta qayta ulanadi. Har renderda emas.
  const [wsToken, setWsToken] = useState<string>(() => {
    try { return localStorage.getItem("accessToken") || "" } catch { return "" }
  })
  useEffect(() => {
    const sync = () => {
      try {
        const t = localStorage.getItem("accessToken") || ""
        setWsToken((prev) => (prev === t ? prev : t))   // bir xil bo'lsa state umuman yangilanmaydi
      } catch { /* ignore */ }
    }
    window.addEventListener("storage", sync)   // boshqa tabda kirish/chiqish
    const id = setInterval(sync, 5000)         // shu tabda kirish (localStorage hodisa bermaydi)
    return () => { window.removeEventListener("storage", sync); clearInterval(id) }
  }, [])

  const wsUrl = useCallback(
    () => (wsToken ? `${WS_URL2}?access_token=${encodeURIComponent(wsToken)}` : WS_URL2),
    [wsToken]
  )
  const { sendMessage, lastMessage, readyState } = useWebSocket(wsUrl, WS_OPTIONS)
  // Tog'a #342: qo'ng'iroq moduli (CallManager) signal yuborishi uchun — ulanish ochiq bo'lsagina
  useEffect(() => {
    ;(window as any).__crmWsSend = readyState === ReadyState.OPEN ? (o: any) => { try { sendMessage(JSON.stringify(o)); return true } catch { return false } } : null
  }, [readyState, sendMessage])

  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  }[readyState]

  // Yagona chat (egasi 2026-09-02)
  const [unreadChat, setUnreadChat] = useState<boolean>(localStorage.getItem("unreadChat") === "true")
  useEffect(() => { localStorage.setItem("unreadChat", unreadChat.toString()) }, [unreadChat])

  const [onlineCounterparties, setOnlineCounterparties] = useState<number[]>([])
  const [onlinePresenceMap, setOnlinePresenceMap] = useState<Record<number, CounterpartyPresence>>({})
  const activeChatIdRef = useRef<number | null>(null)

  const fetchOnlineDetails = useCallback(async () => {
    try {
      const token = localStorage.getItem("accessToken")
      if (token) {
        const res = await axios.get(`${API_URL}Counterparties/OnlineDetails`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (Array.isArray(res.data)) {
          const map: Record<number, CounterpartyPresence> = {}
          const ids: number[] = []

          res.data.forEach((item: any) => {
            const ctId = Number(item.counterpartyId || item.CounterpartyId)
            if (ctId > 0) {
              ids.push(ctId)
              map[ctId] = {
                isOnline: Boolean(item.isOnline ?? item.IsOnline ?? true),
                isInChat: Boolean(item.isInChat ?? item.IsInChat),
                activeChatId: item.activeChatId ?? item.ActiveChatId,
                lastActiveAt: item.lastActiveAt ?? item.LastActiveAt,
              }
            }
          })

          // PERF-05 (2026-09-03): avval har so'rovda YANGI massiv va YANGI obyekt yozilardi —
          // qiymat o'zgarmagan bo'lsa ham kontekst yangilanib, uni ishlatadigan BARCHA
          // komponent qayta chizilardi. Endi faqat haqiqatan farq bo'lsa yoziladi.
          setOnlineCounterparties((prev) =>
            prev.length === ids.length && prev.every((v, i) => v === ids[i]) ? prev : ids
          )
          setOnlinePresenceMap((prev) => {
            const next = { ...prev, ...map }
            const pk = Object.keys(prev)
            if (pk.length === Object.keys(next).length &&
                pk.every((k) => JSON.stringify(prev[+k]) === JSON.stringify(next[+k]))) return prev
            return next
          })
        }
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    fetchOnlineDetails()
    // PERF-05: WS pingi allaqachon presence so'rab turadi (get_online_details), shuning uchun
    // HTTP so'rovi faqat ZAXIRA — 25 soniyadan 2 daqiqaga siyraklashtirildi.
    const interval = setInterval(fetchOnlineDetails, 120000)
    return () => clearInterval(interval)
  }, [fetchOnlineDetails])

  // Ping online on WebSocket connection & periodically
  const sendOnlinePing = useCallback(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}")
      const ctId = user?.counterpartyId || user?.CounterpartyId
      const uId = user?.id || user?.userId || user?.UserId

      if (readyState === ReadyState.OPEN) {
        sendMessage(
          JSON.stringify({
            type: "ping_online",
            counterpartyId: ctId ? Number(ctId) : 0,
            userId: uId ? Number(uId) : 0,
            activeChatId: activeChatIdRef.current || 0,
          })
        )
        sendMessage(
          JSON.stringify({
            type: "get_online_details",
          })
        )
      }
    } catch {
      // ignore
    }
  }, [readyState, sendMessage])

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendOnlinePing()
    }

    const pingInterval = setInterval(sendOnlinePing, 30000)   // PERF-05: keepalive, 15s shart emas
    return () => clearInterval(pingInterval)
  }, [readyState, sendOnlinePing])

  // Chat room presence controls
  const enterChat = useCallback(
    (chatId: number | string) => {
      const numId = Number(chatId)
      if (!numId) return
      activeChatIdRef.current = numId

      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}")
        const ctId = user?.counterpartyId || user?.CounterpartyId
        const uId = user?.id || user?.userId || user?.UserId

        if (readyState === ReadyState.OPEN) {
          sendMessage(
            JSON.stringify({
              type: "enter_chat",
              chatId: numId,
              counterpartyId: ctId ? Number(ctId) : 0,
              userId: uId ? Number(uId) : 0,
            })
          )
        }
      } catch {}
    },
    [readyState, sendMessage]
  )

  const leaveChat = useCallback(
    (chatId?: number | string) => {
      activeChatIdRef.current = null
      try {
        if (readyState === ReadyState.OPEN) {
          sendMessage(
            JSON.stringify({
              type: "leave_chat",
              chatId: chatId ? Number(chatId) : 0,
            })
          )
        }
      } catch {}
    },
    [readyState, sendMessage]
  )

  // Handle incoming socket messages for onlineStatus
  useEffect(() => {
    if (lastMessage?.data) {
      try {
        const parsed = JSON.parse(String(lastMessage.data))
        if (parsed?.nameFunction === "onlineStatus") {
          if (parsed.typeFunction === "list") {
            if (Array.isArray(parsed.data)) {
              setOnlineCounterparties(parsed.data.map(Number))
            }
            if (Array.isArray(parsed.details)) {
              const map: Record<number, CounterpartyPresence> = {}
              parsed.details.forEach((item: any) => {
                const ctId = Number(item.counterpartyId || item.CounterpartyId)
                if (ctId > 0) {
                  map[ctId] = {
                    isOnline: Boolean(item.isOnline ?? item.IsOnline ?? true),
                    isInChat: Boolean(item.isInChat ?? item.IsInChat),
                    activeChatId: item.activeChatId ?? item.ActiveChatId,
                    lastActiveAt: item.lastActiveAt ?? item.LastActiveAt,
                  }
                }
              })
              setOnlinePresenceMap((prev) => ({ ...prev, ...map }))
            }
          } else if (
            parsed.typeFunction === "change" &&
            parsed.data?.counterpartyId !== undefined
          ) {
            const ctId = Number(parsed.data.counterpartyId)
            const isOnline = Boolean(parsed.data.isOnline)
            const isInChat = Boolean(parsed.data.isInChat)
            const activeChatId = parsed.data.activeChatId
            const lastActiveAt = parsed.data.lastActiveAt || new Date().toISOString()

            setOnlineCounterparties((prev) => {
              if (isOnline) {
                return prev.includes(ctId) ? prev : [...prev, ctId]
              } else {
                return prev.filter((id) => id !== ctId)
              }
            })

            setOnlinePresenceMap((prev) => ({
              ...prev,
              [ctId]: {
                isOnline,
                isInChat,
                activeChatId,
                lastActiveAt,
              },
            }))
          }
        }
      } catch {
        // ignore
      }
    }
  }, [lastMessage])

  const isCounterpartyOnline = useCallback(
    (id: number | string | undefined | null) => {
      if (!id) return false
      const ctId = Number(id)
      if (onlinePresenceMap[ctId] !== undefined) {
        return Boolean(onlinePresenceMap[ctId]?.isOnline)
      }
      return onlineCounterparties.includes(ctId)
    },
    [onlineCounterparties, onlinePresenceMap]
  )

  const getCounterpartyPresence = useCallback(
    (id: number | string | undefined | null): CounterpartyPresence | null => {
      if (!id) return null
      const ctId = Number(id)
      if (onlinePresenceMap[ctId]) {
        return onlinePresenceMap[ctId]
      }
      if (onlineCounterparties.includes(ctId)) {
        return {
          isOnline: true,
          isInChat: false,
          activeChatId: null,
          lastActiveAt: new Date().toISOString(),
        }
      }
      return null
    },
    [onlineCounterparties, onlinePresenceMap]
  )

  // Render yo'lida himoyasiz JSON.parse ilovani yiqitardi: server bitta non-JSON
  // frame (plaintext ping, HTML xato sahifasi) yuborsa SyntaxError → ErrorBoundary.
  // Effect'lardagi parse allaqachon try/catch bilan — render yo'li ham shunday.
  const parsedLastMessage = useMemo(() => {
    try {
      return lastMessage?.data ? JSON.parse(String(lastMessage.data)) : null
    } catch {
      return null
    }
  }, [lastMessage])

  return (
    <SocketContext.Provider
      value={{
        connectionStatus,
        unreadChat,
        setUnreadChat,
        onlineCounterparties,
        onlinePresenceMap,
        isCounterpartyOnline,
        getCounterpartyPresence,
        enterChat,
        leaveChat,
        lastMessage: parsedLastMessage,
      }}
    >
      {children}
    </SocketContext.Provider>
  )
}
